export type PackagingKind = 'can' | 'bottle' | 'other';

export const PACKAGING_GROUP_ORDER: PackagingKind[] = ['can', 'bottle', 'other'];

export const PACKAGING_GROUP_LABEL: Record<PackagingKind, string> = {
  can: 'Dosen',
  bottle: 'Flaschen',
  other: 'Other',
};

export function classifyPackaging(text: string): PackagingKind {
  const value = text.toLowerCase();
  if (/\bdose|\bcan\b|dosen/.test(value)) return 'can';
  if (/flasche|flaschen|\bglass\b|longneck|\bbottle/.test(value)) return 'bottle';
  return 'other';
}

export function variantPackaging(variant: {
  gebinde_name: string;
  package_label?: string | null;
}): PackagingKind {
  return classifyPackaging(`${variant.package_label || ''} ${variant.gebinde_name}`);
}

export interface PackagingGroup<T> {
  kind: PackagingKind;
  label: string;
  items: T[];
}

export function groupVariants<T extends {
  gebinde_name: string;
  package_label?: string | null;
}>(variants: T[]): PackagingGroup<T>[] {
  const buckets = new Map<PackagingKind, T[]>();
  for (const kind of PACKAGING_GROUP_ORDER) buckets.set(kind, []);
  for (const variant of variants) {
    buckets.get(variantPackaging(variant))!.push(variant);
  }
  return PACKAGING_GROUP_ORDER
    .map((kind) => ({
      kind,
      label: PACKAGING_GROUP_LABEL[kind],
      items: buckets.get(kind) || [],
    }))
    .filter((group) => group.items.length > 0);
}

export interface ManufacturerProductGroup<T> {
  manufacturer: string;
  items: T[];
}

export function groupProductsByManufacturer<T extends {
  name: string;
  manufacturer_name?: string | null;
}>(products: T[]): ManufacturerProductGroup<T>[] {
  const map = new Map<string, T[]>();
  for (const product of products) {
    const key = product.manufacturer_name || 'Other';
    const list = map.get(key) || [];
    list.push(product);
    map.set(key, list);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([manufacturer, items]) => ({
      manufacturer,
      items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
    }));
}

export function productSelectLabel(product: {
  name: string;
  manufacturer_name?: string | null;
}): string {
  const maker = (product.manufacturer_name || '').trim();
  return maker ? `${maker} - ${product.name}` : product.name;
}

export function sortProductsForSelect<T extends {
  name: string;
  manufacturer_name?: string | null;
}>(products: T[]): T[] {
  return [...products].sort((a, b) =>
    productSelectLabel(a).localeCompare(productSelectLabel(b))
  );
}
